// ─────────────────────────────────────────────────────────────────────────────
// useCustomEvalPresets
//
// Fetches, creates, updates, and deletes user-created eval presets from
// the custom_eval_presets table. Same shape as prop_firm_presets so the
// simulator can use both without modification.
// ─────────────────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useState } from "react";
import { SUPABASE_URL, authFetch } from "../lib/supabase";

const TABLE = `${SUPABASE_URL}/rest/v1/custom_eval_presets`;

export function useCustomEvalPresets(userId) {
  const [presets,  setPresets]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);

  // ── Fetch ────────────────────────────────────────────────────────────────
  const fetchPresets = useCallback(async () => {
    if (!userId) { setPresets([]); setLoading(false); return; }
    try {
      const res  = await authFetch(`${TABLE}?user_id=eq.${userId}&order=created_at.asc&select=*`);
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || "Failed to load custom presets");
      setPresets(Array.isArray(data) ? data : []);
      setError(null);
    } catch (e) {
      console.error("[useCustomEvalPresets] fetch:", e.message);
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { fetchPresets(); }, [fetchPresets]);

  // ── Create ───────────────────────────────────────────────────────────────
  const createPreset = useCallback(async (fields) => {
    if (!userId) return null;
    try {
      const res = await authFetch(TABLE, {
        method: "POST",
        headers: { "Content-Type": "application/json", Prefer: "return=representation" },
        body: JSON.stringify({ ...fields, user_id: userId }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || "Failed to create preset");
      const created = Array.isArray(data) ? data[0] : data;
      setPresets(prev => [...prev, created]);
      return created;
    } catch (e) {
      console.error("[useCustomEvalPresets] create:", e.message);
      throw e;
    }
  }, [userId]);

  // ── Update ───────────────────────────────────────────────────────────────
  const updatePreset = useCallback(async (id, fields) => {
    try {
      const res = await authFetch(
        `${TABLE}?id=eq.${id}&user_id=eq.${userId}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json", Prefer: "return=representation" },
          body: JSON.stringify({ ...fields, updated_at: new Date().toISOString() }),
        }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || "Failed to update preset");
      const updated = Array.isArray(data) ? data[0] : data;
      setPresets(prev => prev.map(p => p.id === id ? { ...p, ...updated } : p));
      return updated;
    } catch (e) {
      console.error("[useCustomEvalPresets] update:", e.message);
      throw e;
    }
  }, [userId]);

  // ── Delete ───────────────────────────────────────────────────────────────
  const deletePreset = useCallback(async (id) => {
    try {
      const res = await authFetch(
        `${TABLE}?id=eq.${id}&user_id=eq.${userId}`,
        { method: "DELETE" }
      );
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.message || "Failed to delete preset");
      }
      setPresets(prev => prev.filter(p => p.id !== id));
    } catch (e) {
      console.error("[useCustomEvalPresets] delete:", e.message);
      throw e;
    }
  }, [userId]);

  return { presets, loading, error, createPreset, updatePreset, deletePreset, refetch: fetchPresets };
}
